import type { TableSelectedCell } from '@/components/custom/erp/input-table-v2';
import { useTableRows } from '@/components/custom/erp/input-table-v2';
import { calculateRowKhoiLuong } from '../../../utils/calc-util';

export type SelectedCellInfo = TableSelectedCell;

type DetailRowBase = {
  uuid: string;
  line?: number;
  ct_km?: string;
  khoi_luong?: number;
  [key: string]: BanHangValue;
};

const ensureRowDefaults = <T extends DetailRowBase>(row: Partial<T>, index: number): T => {
  const khoiLuong =
    row?.khoi_luong !== undefined && row?.khoi_luong !== null
      ? Number(row.khoi_luong) || 0
      : calculateRowKhoiLuong(row?.so_luong, row?.ma_vt_data, row?.dvt || row?.ma_vt_data?.dvt);

  return {
    ...row,
    uuid: row?.uuid && row.uuid !== '' ? row.uuid : `temp-${Date.now()}-${index}`,
    ct_km: row?.ct_km ?? '0',
    khoi_luong: khoiLuong,
    ...(row?.line !== undefined && { line: row.line || index + 1 })
  } as T;
};

const normalizeInitialRows = <T extends DetailRowBase>(rows: T[] = []): T[] => {
  if (rows.length > 0) {
    return rows.map((row, index) => ensureRowDefaults(row, index)) as T[];
  }

  return [
    ensureRowDefaults(
      {
        uuid: `temp-${Date.now()}`,
        line: 1
      } as T,
      0
    )
  ] as T[];
};

export function useDetailRows<T extends DetailRowBase>(
  initialRows: T[] = [],
  onChange?: (rows: T[]) => void,
  onFieldValueChange?: (row: T, field: string, value: BanHangValue) => Partial<T>
) {
  const normalizedRows = normalizeInitialRows(initialRows);

  const {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handleMoveRow,
    handlePasteRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  } = useTableRows<T>({
    initialRows: normalizedRows,
    onChange,
    onFieldValueChange,
    defaultField: 'dien_giai'
  });

  return {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  };
}
